import "./QuestionGroupEditor.less";

import {
    qualityFormActions,
    selectEligibleQuestionsForVisibilityConditionsForQuestionGroup,
    selectIsFormWeighted,
    selectQuestionGroup,
    toggleConditionalDisplay,
} from "features/qualityForm/qualityFormSlice";
import {
    selectEvaluationFormType,
    selectFormType,
} from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import { useStrings } from "features/i18n/useStrings";
import { shallowEqual } from "react-redux";
import {
    GuxButton,
    GuxFormFieldTextLike,
    GuxIcon,
    GuxFormFieldCheckbox,
} from "genesys-spark-components-react";

import { PropertySection } from "../common/PropertySection";
import { QuestionGroupMetaData } from "./QuestionGroup";
import strings from "./QuestionGroupEditor.strings";
import { QuestionGroupVisibilityConditions } from "../common/VisibilityConditions/VisibilityConditionContainer";
import FormType from "enums/FormType";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
    selectIsAnAnswersDefaultToSelected,
    selectIsQuestionGroupNamePresent,
    selectIsQuestionGroupNameTooLong,
} from "features/qualityForm/qualityFormValidations";
import { DefaultLimits } from "constants/DefaultLimits";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";
import { EvaluationFormEvaluationFormTypeEnum } from "@genesys/cloud-frontend/models";
import { ErrorStatement } from "../common/ErrorStatement/ErrorStatement";
import { AnswersDefaultToSection } from "./AnswersDefaultToSection";
import { toGuxDisabled } from "util/componentUtil";

export function QuestionGroupEditor({
    questionGroupId,
    onDone,
    onDelete,
}: {
    questionGroupId: string;
    onDone: React.MouseEventHandler<HTMLGuxButtonElement>;
    onDelete?: () => void;
}) {
    const t = useStrings(strings);

    const formType = useAppSelector((state) => selectFormType(state));

    const enableDefaultSelections = useAppSelector(
        (state) => selectFormType(state) !== FormType.SURVEY
    );

    const questionGroup = useAppSelector((state) => {
        const {
            name,
            naEnabled,
            defaultAnswersToHighest,
            defaultAnswersToNA,
            defaultAnswersTo,
            visibilityCondition,
        } = selectQuestionGroup(state, questionGroupId) ?? {};
        return {
            name,
            naEnabled,
            defaultAnswersToHighest,
            defaultAnswersToNA,
            defaultAnswersTo,
            isConditionallyVisible:
                visibilityCondition &&
                visibilityCondition.predicates?.length > 0,
        };
    }, shallowEqual);

    const allowVisibilityConditions = useAppSelector(
        (state) =>
            selectEligibleQuestionsForVisibilityConditionsForQuestionGroup(
                state,
                questionGroupId,
                true
            )?.length ?? 0 > 0
    );

    const nameErrorMessage = useAppSelector((state) =>
        !selectIsQuestionGroupNamePresent(state, questionGroupId)
            ? t.error.nameRequired
            : selectIsQuestionGroupNameTooLong(state, questionGroupId)
            ? t.error.nameTooLong({
                  maxQuestionGroupNameLength:
                      DefaultLimits.maxQuestionGroupNameLength,
              })
            : undefined
    );
    const dispatch = useAppDispatch();

    const isWeighted = useAppSelector(selectIsFormWeighted);

    // Import all FTs
    const { isVoiceSurveyFeatureEnabled, isMultipleSelectFTEnabled } =
        useAppSelector((state) => ({
            isVoiceSurveyFeatureEnabled: selectFeatureToggle(
                state,
                FeatureToggle.VOICE_SURVEY
            ),
            isMultipleSelectFTEnabled: selectFeatureToggle(
                state,
                FeatureToggle.MULTIPLE_SELECT
            ),
        }));

    const isAnAnswersDefaultToSelected = useAppSelector((state) =>
        selectIsAnAnswersDefaultToSelected(state, questionGroupId)
    );

    const isAutomatedEvaluationForm =
        useAppSelector(selectEvaluationFormType) ===
        EvaluationFormEvaluationFormTypeEnum.Automated;

    const showAnswersDefaultToRequiredError =
        isAutomatedEvaluationForm && !isAnAnswersDefaultToSelected;

    return (
        <>
            <div className="QuestionGroupEditor">
                <GuxFormFieldTextLike clearable label-position="above">
                    <label slot="label">{t.labels.questionGroupName}</label>
                    <input
                        slot="input"
                        type="text"
                        required
                        value={questionGroup.name}
                        onInput={(event) => {
                            dispatch(
                                qualityFormActions.setQuestionGroupName({
                                    groupId: questionGroupId,
                                    newName: (
                                        event.target as HTMLTextAreaElement
                                    ).value,
                                })
                            );
                        }}
                    />
                    {nameErrorMessage && (
                        <span slot="error" role="alert">
                            {nameErrorMessage}
                        </span>
                    )}
                </GuxFormFieldTextLike>
                <QuestionGroupMetaData
                    questionGroupId={questionGroupId}
                    editQuestionGroupWeight={isWeighted}
                />
            </div>
            {enableDefaultSelections && (
                <PropertySection title={t.labels.questionGroupPropertiesTitle}>
                    <div className="property-section-checkboxes">
                        <GuxFormFieldCheckbox>
                            <input
                                slot="input"
                                type="checkbox"
                                onChange={() => {
                                    dispatch(
                                        qualityFormActions.toggleQuestionGroupNAEnabled(
                                            {
                                                groupId: questionGroupId,
                                            }
                                        )
                                    );
                                }}
                                checked={questionGroup?.naEnabled}
                                className="property-section-checkbox"
                            />
                            <label slot="label">{t.labels.enableNA}</label>
                        </GuxFormFieldCheckbox>
                    </div>
                </PropertySection>
            )}
            {allowVisibilityConditions && (
                <PropertySection title={t.labels.logic.title}>
                    <div className="property-section-checkboxes">
                        <GuxFormFieldCheckbox>
                            <input
                                slot="input"
                                type="checkbox"
                                name="displayConditionally"
                                checked={questionGroup.isConditionallyVisible}
                                onClick={() => {
                                    dispatch(
                                        toggleConditionalDisplay(
                                            questionGroupId,
                                            "questionGroup",
                                            isMultipleSelectFTEnabled
                                        )
                                    );
                                }}
                                className="property-section-checkbox"
                            />
                            <label slot="label">
                                {isVoiceSurveyFeatureEnabled
                                    ? formType === FormType.SURVEY
                                        ? t.labels.logic.includeConditionally
                                        : t.labels.logic.displayConditionally
                                    : t.labels.logic.displayConditionally}
                            </label>
                        </GuxFormFieldCheckbox>
                    </div>
                    {questionGroup.isConditionallyVisible && (
                        <QuestionGroupVisibilityConditions
                            questionGroupId={questionGroupId}
                        />
                    )}
                </PropertySection>
            )}
            {enableDefaultSelections && (
                <PropertySection title={t.labels.groupScoringTitle}>
                    <AnswersDefaultToSection
                        questionGroupId={questionGroupId}
                        questionGroup={questionGroup}
                    />
                    {showAnswersDefaultToRequiredError && (
                        <span
                            className="AnswersDefaultToRequired"
                            data-testid="answers-default-to-required"
                        >
                            <ErrorStatement
                                errorMessage={t.error.answersDefaultToRequired}
                            />
                        </span>
                    )}
                </PropertySection>
            )}
            <div className="action-buttons">
                <GuxButton
                    disabled={toGuxDisabled(onDelete === undefined)}
                    aria-disabled={toGuxDisabled(onDelete === undefined)}
                    onClick={() => {
                        onDelete?.();
                    }}
                >
                    <GuxIcon icon-name="delete" decorative />
                    <span>{t.labels.delete}</span>
                </GuxButton>
                <GuxButton accent="primary" onClick={onDone}>
                    {t.labels.done}
                </GuxButton>
            </div>
        </>
    );
}
